import { useState } from "react";
import { useMutation } from "@apollo/client";
import { showSnackbar } from "@/lib/snackbarUtils";
import { UPDATE_TASK_MUTATION, type UpdateTaskResponse, type BackofficeCRMUpdateTaskFields } from "./mutations";
import type { CRMTask } from "@/types";
import useAmplitude from "@/hooks/useAmplitude";
import { TASK_ANALYTICS_EVENTS, getTaskAnalyticsData, type TaskAnalyticsSource } from "../_utils/amplitude";

type UseUpdateTaskOptions = {
  task?: CRMTask;
  source?: TaskAnalyticsSource;
};

export default function useUpdateTask(taskId: string, options: UseUpdateTaskOptions = {}) {
  const [error, setError] = useState<string | null>(null);
  const { trackEvent } = useAmplitude();

  const [updateTask, { loading }] = useMutation<UpdateTaskResponse>(UPDATE_TASK_MUTATION);

  async function updateAssignee(assignedToId: string) {
    return update({ assignedToId });
  }

  async function update(fields: BackofficeCRMUpdateTaskFields) {
    setError(null);

    try {
      const result = await updateTask({
        variables: { input: { id: taskId, ...fields } }
      });

      if (result.data?.updateTask?.success) {
        if (options.source) {
          trackEvent(TASK_ANALYTICS_EVENTS.updated, {
            ...(options.task ? getTaskAnalyticsData(options.task, options.source) : { taskId, source: options.source }),
            fieldsChanged: Object.keys(fields)
          });
        }
        showSnackbar("Task updated");
        return true;
      }

      setError(result.data?.updateTask?.message || "Failed to update task");
      return false;
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to update task. Please try again.";
      setError(message);
      return false;
    }
  }

  return { updateAssignee, update, loading, error };
}
